import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiClock, FiInfo, FiCheckCircle, FiShield, FiBook, 
  FiAlertTriangle, FiUsers, FiFileText, FiChevronDown, FiChevronUp, FiArrowRight 
} = FiIcons;

const LegalBadge = ({ text }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-tight">
    {text}
  </span>
);

const InvestigationGuide = ({ isEmbedded = false }) => {
  const [activeStep, setActiveStep] = useState(0); // 0 = Oversikt
  const [expandedGuidelines, setExpandedGuidelines] = useState({
    principles: false,
    deadlines: false,
    documentation: false
  });

  const toggleGuideline = (key) => {
    setExpandedGuidelines(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const investigationSteps = [
    { id: 1, title: "Møte med varsler", duration: "Innen 1 uke", priority: "Høy", color: "blue" },
    { id: 2, title: "Møte med varslet person", duration: "Innen 1 uke etter møte 1", priority: "Høy", color: "orange" },
    { id: 3, title: "Vitneavhør", duration: "Etter behov", priority: "Middels", color: "green" },
    { id: 4, title: "Vurdering og konklusjon", duration: "Uten opphold", priority: "Høy", color: "purple" },
    { id: 5, title: "Oppfølging", duration: "Kontinuerlig", priority: "Middels", color: "indigo" }
  ];

  return (
    <div className={`bg-white dark:bg-gray-900 ${isEmbedded ? 'h-full' : 'min-h-screen p-8'}`}>
      <div className="max-w-5xl mx-auto space-y-12">
        
        {/* --- NIVÅ 1: RETNINGSLINJER --- */}
        <section className="space-y-4">
          <h2 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-3">
            <SafeIcon icon={FiShield} className="text-blue-600" />
            Retningslinjer – Generelle prinsipper
          </h2>

          {/* Accordion 1: Prinsipper */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-gray-800">
            <button 
              onClick={() => toggleGuideline('principles')}
              className="w-full flex justify-between items-center p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <span className="font-bold text-gray-900 dark:text-white">Generelle prinsipper for saksbehandling</span>
              <SafeIcon icon={expandedGuidelines.principles ? FiChevronUp : FiChevronDown} />
            </button>
            <AnimatePresence>
              {expandedGuidelines.principles && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-t dark:border-gray-700">
                  <div className="p-6 space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <ul className="grid md:grid-cols-2 gap-4">
                      <li className="flex gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" /> Ta saken på alvor fra første øyeblikk</li>
                      <li className="flex gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" /> Behandle saken konfidensielt</li>
                      <li className="flex gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" /> Følg kontradiksjonsprinsippet – begge parter har rett til å gi sin versjon</li>
                      <li className="flex gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" /> Vær objektive og nøytrale i hele prosessen</li>
                      <li className="flex gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" /> Dokumenter alt skriftlig</li>
                      <li className="flex gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" /> Respekter begge parters rettigheter</li>
                    </ul>
                    <div className="mt-4 pt-4 border-t dark:border-gray-700">
                      <p className="text-xs italic text-gray-500">
                        <LegalBadge text="Juridisk forankring" /> AML kap. 2A (varsling), AML §4-3 (psykososialt arbeidsmiljø), krav til kontradiksjon/likebehandling.
                      </p>
                      <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                        <strong>Presisering:</strong> Konfidensialitet betyr ikke hemmelighold for partene – men begrenset innsyn iht. behov og regelverk.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Accordion 2: Tidsfrister */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-gray-800">
            <button 
              onClick={() => toggleGuideline('deadlines')}
              className="w-full flex justify-between items-center p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <span className="font-bold text-gray-900 dark:text-white">Tidsfrister og fremdrift</span>
              <SafeIcon icon={expandedGuidelines.deadlines ? FiChevronUp : FiChevronDown} />
            </button>
            <AnimatePresence>
              {expandedGuidelines.deadlines && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-t dark:border-gray-700">
                  <div className="p-6 space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <div className="grid md:grid-cols-2 gap-y-3 gap-x-8">
                      <div className="flex justify-between border-b pb-1 dark:border-gray-700"><span>Første møte med varsler:</span> <span className="font-bold">Innen 1 uke</span></div>
                      <div className="flex justify-between border-b pb-1 dark:border-gray-700"><span>Møte med varslet person:</span> <span className="font-bold">Innen 1 uke etter første møte</span></div>
                      <div className="flex justify-between border-b pb-1 dark:border-gray-700"><span>Vitneavhør:</span> <span className="font-bold">Så snart som praktisk mulig</span></div>
                      <div className="flex justify-between border-b pb-1 dark:border-gray-700"><span>Konklusjon:</span> <span className="font-bold">Uten unødvendig opphold</span></div>
                      <div className="flex justify-between border-b pb-1 dark:border-gray-700"><span>Skriftlig tilbakemelding:</span> <span className="font-bold">Senest 2 uker etter siste samtale</span></div>
                    </div>
                    <p className="mt-4 text-xs text-orange-600 font-medium italic">
                      <strong>Kommentar:</strong> Tidsfrister kan forkortes ved høy risiko (fare for helse/sikkerhet), og forlenges ved tung dokumentasjon – men alltid «uten unødvendig opphold».
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Accordion 3: Dokumentasjon */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-gray-800">
            <button 
              onClick={() => toggleGuideline('documentation')}
              className="w-full flex justify-between items-center p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <span className="font-bold text-gray-900 dark:text-white">Krav til dokumentasjon</span>
              <SafeIcon icon={expandedGuidelines.documentation ? FiChevronUp : FiChevronDown} />
            </button>
            <AnimatePresence>
              {expandedGuidelines.documentation && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-t dark:border-gray-700">
                  <div className="p-6 space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <ul className="space-y-2">
                      <li className="flex gap-2"><SafeIcon icon={FiFileText} className="text-blue-500 mt-1" /> Alle møter skal referatføres</li>
                      <li className="flex gap-2"><SafeIcon icon={FiFileText} className="text-blue-500 mt-1" /> Alle vurderinger skal dokumenteres</li>
                      <li className="flex gap-2"><SafeIcon icon={FiFileText} className="text-blue-500 mt-1" /> Beslutninger skal begrunnes skriftlig</li>
                      <li className="flex gap-2"><SafeIcon icon={FiFileText} className="text-blue-500 mt-1" /> Dokumentasjon lagres i HMS-systemet</li>
                      <li className="flex gap-2"><SafeIcon icon={FiFileText} className="text-blue-500 mt-1" /> Taushetsplikt og tilgangsstyring skal ivaretas</li>
                    </ul>
                    <div className="mt-4 pt-4 border-t dark:border-gray-700">
                      <p className="text-xs italic text-gray-500">
                        <LegalBadge text="Juridisk forankring" /> AML §5-6 (undersøkelse/tiltak krever dokumentert sporbarhet).
                      </p>
                      <p className="mt-2 text-xs text-blue-600 font-medium">
                        <strong>Presisering:</strong> Dokumentasjon skal skille tydelig mellom fakta, vurdering og konklusjon.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* --- NIVÅ 2: VEILEDER FOR UTREDNING --- */}
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">Veileder for utredning av varslingssak (V2)</h2>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl overflow-hidden shadow-xl">
            {/* Tabs Navigation */}
            <div className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 p-6">
              <div className="flex flex-wrap gap-2">
                <button 
                  onClick={() => setActiveStep(0)} 
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${activeStep === 0 ? 'bg-white dark:bg-gray-700 border-blue-500 text-blue-600 shadow-sm' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  Oversikt
                </button>
                {investigationSteps.map(step => (
                  <button 
                    key={step.id} 
                    onClick={() => setActiveStep(step.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${activeStep === step.id ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 text-blue-600 shadow-sm' : 'border-transparent text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    Steg {step.id}: {step.title.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>

            {/* Content Area */}
            <div className="p-8 min-h-[600px]">
              <AnimatePresence mode="wait">
                
                {/* 0. OVERSIKT */}
                {activeStep === 0 && (
                  <motion.div key="oversikt" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-10">
                    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-6 rounded-r-2xl">
                      <h3 className="text-amber-800 dark:text-amber-400 font-black flex items-center gap-2 mb-2">
                        <SafeIcon icon={FiAlertTriangle} /> VIKTIG INFORMASJON
                      </h3>
                      <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                        Denne veilederen følger arbeidsmiljølovens bestemmelser og etablert praksis for håndtering av varslingssaker. Alle steg må dokumenteres grundig for å sikre etterlevelse ved et eventuelt tilsyn eller rettslig prøving.
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">Dine plikter som arbeidsgiver</h4>
                        <ul className="space-y-3">
                          {['Ta varselet på alvor', 'Behandle saken konfidensielt', 'Følge kontradiksjonsprinsippet', 'Gjenopprette forsvarlig arbeidsmiljø'].map((p, i) => (
                            <li key={i} className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                              <SafeIcon icon={FiCheckCircle} className="text-blue-500" /> {p}
                            </li>
                          ))}
                        </ul>
                        <p className="text-[10px] text-gray-400 italic">Hjemmel: AML §5-6, AML §4-1 / §4-3</p>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">Involvertes rettigheter</h4>
                        <ul className="space-y-3">
                          {['Rett til å gi sin versjon', 'Rett til å ha med tillitsperson', 'Beskyttelse mot gjengjeldelse', 'Rett til informasjon om prosessen'].map((p, i) => (
                            <li key={i} className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                              <SafeIcon icon={FiCheckCircle} className="text-indigo-500" /> {p}
                            </li>
                          ))}
                        </ul>
                        <p className="text-[10px] text-gray-400 italic">Hjemmel: AML kap. 2A, AML §2-3</p>
                      </div>
                    </div>

                    <div className="pt-8 border-t dark:border-gray-700">
                      <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs mb-6">Prosessoversikt</h4>
                      <div className="space-y-3">
                        {investigationSteps.map(s => (
                          <div key={s.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border dark:border-gray-700">
                            <div className="flex items-center gap-4">
                              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{s.id}</span>
                              <div>
                                <p className="text-sm font-bold text-gray-900 dark:text-white">{s.title}</p>
                                <p className="text-xs text-gray-500">{s.duration}</p>
                              </div>
                            </div>
                            <span className="text-[10px] font-black text-red-600 uppercase tracking-widest bg-red-50 px-2 py-1 rounded">{s.priority} prioritet</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* NY TILLEGGSSEKSJON: MAPPING */}
                    <div className="bg-blue-900/10 border border-blue-200 dark:border-blue-800 p-6 rounded-2xl">
                      <h4 className="font-black text-blue-900 dark:text-blue-200 uppercase tracking-widest text-xs mb-4">Kobling til Utredningsskjema (10 steg)</h4>
                      <div className="grid sm:grid-cols-2 gap-4 text-[11px] font-medium text-blue-800 dark:text-blue-300">
                        <div className="flex items-center gap-2 bg-white/50 dark:bg-gray-800 p-2 rounded-lg">
                          <span className="font-black text-blue-600">Skjema 1–2:</span> Veileder Steg 1 (mottak + vurdering)
                        </div>
                        <div className="flex items-center gap-2 bg-white/50 dark:bg-gray-800 p-2 rounded-lg">
                          <span className="font-black text-blue-600">Skjema 3–6:</span> Veileder Steg 1–3 (møter, vitner, bevis)
                        </div>
                        <div className="flex items-center gap-2 bg-white/50 dark:bg-gray-800 p-2 rounded-lg">
                          <span className="font-black text-blue-600">Skjema 7–8:</span> Veileder Steg 4 (analyse & konklusjon)
                        </div>
                        <div className="flex items-center gap-2 bg-white/50 dark:bg-gray-800 p-2 rounded-lg">
                          <span className="font-black text-blue-600">Skjema 9–10:</span> Veileder Steg 5 (oppfølging & rapport)
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 1. MØTE MED VARSLER */}
                {activeStep === 1 && (
                  <motion.div key="steg1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-8">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-2xl p-6">
                      <h4 className="text-blue-700 dark:text-blue-400 font-black mb-4 flex items-center gap-2 underline underline-offset-4">Forberedelser før møtet</h4>
                      <ul className="space-y-3 text-sm text-blue-900 dark:text-blue-200">
                        <li className="flex gap-3"><span className="text-blue-400">•</span> Innkall varsler til møte senest innen en uke</li>
                        <li className="flex gap-3"><span className="text-blue-400">•</span> Informer om at varsler kan ta med tillitsperson</li>
                        <li className="flex gap-3"><span className="text-blue-400">•</span> Forbered en objektiv og trygg møteramme</li>
                        <li className="flex gap-3"><span className="text-blue-400">•</span> Sørg for at du som leder er nøytral i saken</li>
                      </ul>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-gray-900 dark:text-white font-black text-lg">Møteagenda og spørsmål</h4>
                      <div className="bg-gray-50 dark:bg-gray-900/30 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 italic text-gray-600 dark:text-gray-400 text-sm leading-relaxed border-l-4 border-l-blue-500">
                        "Som arbeidsgiver har jeg taushetsplikt om informasjonen du gir, men jeg får også en handlingsplikt hvis jeg får kunnskap om trakassering som kan true et forsvarlig arbeidsmiljø. I så fall plikter jeg å gjenopprette et fullt forsvarlig arbeidsmiljø."
                      </div>

                      <div className="grid md:grid-cols-2 gap-8 mt-8">
                        <div className="space-y-4">
                          <h5 className="font-bold text-gray-900 dark:text-white text-sm">Nøkkelspørsmål til varsler:</h5>
                          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 list-disc pl-4">
                            <li>Hva har faktisk skjedd?</li>
                            <li>Når skjedde det?</li>
                            <li>I hvilken sammenheng?</li>
                            <li>Var det flere personer tilstede, og hvem var det?</li>
                            <li>Hvordan opplevde du det?</li>
                            <li>Sa du ifra til vedkommende i situasjonen?</li>
                            <li>Hva ønsker varsler for å stoppe atferden?</li>
                          </ul>
                        </div>
                        <div className="space-y-4">
                          <h5 className="font-bold text-gray-900 dark:text-white text-sm">Forventningsavklaring:</h5>
                          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 list-disc pl-4">
                            <li>Hva ønsker varsler på bakgrunn av varselet?</li>
                            <li>Hva kan arbeidsgiver gjøre for varsler mens saken pågår?</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-900 text-white p-6 rounded-2xl">
                      <h4 className="font-bold mb-4 text-blue-400">Viktige punkter å huske:</h4>
                      <ul className="space-y-2 text-xs opacity-80 grid md:grid-cols-2 gap-x-6">
                        <li>• Skriv detaljert referat fra møtet</li>
                        <li>• Vær nøytral og ikke ta stilling til skyld</li>
                        <li>• Forklar videre fremgangsmåte (kontradiksjon)</li>
                        <li>• Informer om konfidensialitet og lojalitetsplikt</li>
                        <li>• Gi varsler trygghet om prosessen</li>
                      </ul>
                      <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-2">
                        <LegalBadge text="AML kap. 2A" /> <LegalBadge text="AML §4-3" /> <LegalBadge text="AML §5-6" /> <LegalBadge text="AML §2-3" />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 2. MØTE MED VARSLET PERSON */}
                {activeStep === 2 && (
                  <motion.div key="steg2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-8">
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-2xl p-6">
                      <h4 className="text-orange-700 dark:text-orange-400 font-black mb-4">Forberedelser</h4>
                      <ul className="space-y-3 text-sm text-orange-900 dark:text-orange-200">
                        <li className="flex gap-3"><span className="text-orange-400">•</span> Innkall innen en uke etter møte med varsler</li>
                        <li className="flex gap-3"><span className="text-orange-400">•</span> Informer om at vedkommende kan ha med tillitsperson</li>
                        <li className="flex gap-3"><span className="text-orange-400">•</span> Forbered objektiv fremstilling av påstander</li>
                        <li className="flex gap-3 font-bold"><span className="text-orange-400">•</span> Vedkommende har rett til å vite hvem som har varslet</li>
                      </ul>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">Nøkkelspørsmål</h4>
                        <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                          <li>• Hva er din versjon av det som ble varslet?</li>
                          <li>• I hvilken sammenheng skjedde det?</li>
                          <li>• Var det flere personer tilstede?</li>
                          <li>• Hvordan opplevde du de påståtte hendelsene?</li>
                        </ul>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">Ved uenighet</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border dark:border-gray-700">
                          "Forstod du at varsler opplevde det som ubehagelig/krenkende?" <br/><br/>
                          Dette er viktig for å vurdere intensjon vs. virkning i saken.
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-900 text-white p-6 rounded-2xl space-y-4">
                      <h4 className="font-bold text-orange-400">Viktige retningslinjer:</h4>
                      <div className="grid md:grid-cols-2 gap-4 text-xs opacity-80">
                        <p>• Behandle vedkommende med respekt og verdighet</p>
                        <p>• Ikke konkluder eller ta stilling i møtet</p>
                        <p>• Skriv detaljert referat</p>
                        <p>• Informer om konfidensialitetskrav</p>
                      </div>
                      <div className="pt-4 border-t border-white/10 flex flex-wrap gap-2">
                        <LegalBadge text="Kontradiksjon" /> <LegalBadge text="AML kap. 2A" /> <LegalBadge text="AML §4-3" /> <LegalBadge text="AML §5-6" />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 3. VITNEAVHØR */}
                {activeStep === 3 && (
                  <motion.div key="steg3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-8">
                    <div className="space-y-4">
                      <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">Når skal vitner innkalles?</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {['Når parter har oppgitt konkrete vitner', 'For å avklare faktiske forhold', 'Når det er motstridende forklaringer', 'For å få objektiv informasjon om hendelser'].map((p, i) => (
                          <div key={i} className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800 text-sm font-medium">
                            <SafeIcon icon={FiUsers} className="text-green-600" /> {p}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <h5 className="font-bold text-gray-900 dark:text-white">Fremgangsmåte forberedelser:</h5>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300 list-disc pl-4">
                          <li>Innkall vitner individuelt</li>
                          <li>Forklar formålet med samtalen</li>
                          <li>Informer om konfidensialitet</li>
                          <li>Vitner kan ha med tillitsperson</li>
                        </ul>
                      </div>
                      <div className="space-y-4">
                        <h5 className="font-bold text-gray-900 dark:text-white">Spørsmål til vitner:</h5>
                        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300 list-disc pl-4">
                          <li>Hva har du observert?</li>
                          <li>Når skjedde det du observerte?</li>
                          <li>Hvor skjedde det?</li>
                          <li>Hvem var tilstede?</li>
                          <li>Har du observert lignende tidligere?</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-slate-50 dark:bg-gray-800 p-6 rounded-2xl border dark:border-gray-700">
                      <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs mb-4">Dokumentasjon av vitneutsagn</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Skriv detaljert referat. Skille mellom fakta og vitners tolkninger. Dokumenter alle relevante detaljer og la vitnet bekrefte referatet.</p>
                      <div className="pt-4 border-t dark:border-gray-700">
                        <LegalBadge text="AML §5-6" /> <LegalBadge text="Dokumentert prosess" />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 4. VURDERING OG KONKLUSJON */}
                {activeStep === 4 && (
                  <motion.div key="steg4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-8">
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-2xl p-6">
                      <h4 className="text-purple-700 dark:text-purple-400 font-black mb-4">Vurderingsprosess</h4>
                      <ul className="space-y-3 text-sm text-purple-900 dark:text-purple-200">
                        <li className="flex gap-3">• Søk råd hos nærmeste leder og/eller bedriftshelsetjenesten</li>
                        <li className="flex gap-3">• Vurder all innsamlet informasjon objektivt</li>
                        <li className="flex gap-3">• Gjør en helhetsvurdering av bevisene</li>
                        <li className="flex gap-3">• Konkluder uten unødvendig opphold</li>
                      </ul>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-2xl p-6 bg-white dark:bg-gray-800 shadow-sm">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-4">Alternativ A: Ingen hold i varselet</h4>
                        <p className="text-sm text-gray-500 mb-6">Saken avsluttes uten videre tiltak.</p>
                        <h5 className="text-xs font-black text-gray-400 uppercase mb-3">Oppfølging:</h5>
                        <ul className="text-xs space-y-2 text-gray-600 dark:text-gray-400">
                          <li>• Informer begge parter skriftlig</li>
                          <li>• Dokumenter konklusjonen grundig</li>
                          <li>• Planlegg oppfølgingssamtaler</li>
                        </ul>
                      </div>
                      <div className="border border-purple-200 dark:border-purple-800 rounded-2xl p-6 bg-purple-50/30 dark:bg-purple-900/10 shadow-sm">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-4">Alternativ B: Forhold bekreftet</h4>
                        <p className="text-sm text-gray-500 mb-6">Forholdsmessige tiltak må iverksettes.</p>
                        <h5 className="text-xs font-black text-purple-400 uppercase mb-3">Mulige tiltak:</h5>
                        <ul className="text-xs space-y-2 text-gray-600 dark:text-gray-400">
                          <li>• <strong>Milde:</strong> Skriftlig advarsel, veiledning, opplæring</li>
                          <li>• <strong>Moderate:</strong> Fratakelse av ansvar, omplassering</li>
                          <li>• <strong>Alvorlige:</strong> Oppsigelse, avskjed, politianmeldelse</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-gray-900 text-white p-6 rounded-2xl">
                      <h4 className="font-bold mb-4 text-purple-400 tracking-wide uppercase text-xs">Krav til reaksjonen:</h4>
                      <ul className="space-y-2 text-xs opacity-80">
                        <li>• Reaksjonen skal være forholdsmessig</li>
                        <li>• Følg saklighetskravet etter arbeidsmiljøloven</li>
                        <li>• Reaksjon og begrunnelse skal nedtegnes skriftlig</li>
                        <li>• Send skriftlig tilbakemelding til begge parter</li>
                        <li>• Vurder politianmeldelse ved straffbare forhold</li>
                      </ul>
                      <div className="mt-6 pt-4 border-t border-white/10 flex flex-wrap gap-2">
                        <LegalBadge text="AML §4-3" /> <LegalBadge text="AML §5-6" /> <LegalBadge text="AML kap. 2A" />
                      </div>
                      <p className="mt-4 text-[10px] text-gray-400 italic font-medium">
                        <strong>Generisk presisering:</strong> Ved andre varseltyper enn trakassering brukes tilsvarende prinsipp med forholdsmessige tiltak (organisatoriske, prosessuelle, disiplinære der relevant).
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* 5. OPPFØLGING */}
                {activeStep === 5 && (
                  <motion.div key="steg5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-8">
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-2xl p-6">
                      <h4 className="text-indigo-700 dark:text-indigo-400 font-black mb-4">Oppfølgingsplan</h4>
                      <ul className="space-y-3 text-sm text-indigo-900 dark:text-indigo-200">
                        <li className="flex gap-3">• Lag oppfølgingsplan for begge parter uansett utfall</li>
                        <li className="flex gap-3">• Sikre at planen gjennomføres</li>
                        <li className="flex gap-3">• Vurder endringer i arbeidsforholdet</li>
                        <li className="flex gap-3">• Styrk HMS-rutiner for å hindre gjentakelse</li>
                      </ul>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">For varsler</h4>
                        <div className="p-4 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl space-y-2 text-xs text-gray-600 dark:text-gray-400">
                          <p>• Regelmessige oppfølgingssamtaler</p>
                          <p>• Vurder behov for støttetiltak</p>
                          <p>• Overvåk arbeidsmiljøet rundt varsler</p>
                          <p>• Sikre at det ikke forekommer gjengjeldelse</p>
                          <p>• Evaluér effekten av iverksatte tiltak</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">For varslet person</h4>
                        <div className="p-4 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl space-y-2 text-xs text-gray-600 dark:text-gray-400">
                          <p>• Oppfølging av iverksatte tiltak</p>
                          <p>• Vurder behov for veiledning/opplæring</p>
                          <p>• Overvåk atferd og samarbeid</p>
                          <p>• Sikre forståelse av forventninger</p>
                          <p>• Evaluér fremgang og utvikling</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 pt-8">
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">HMS-dokumentasjon</h4>
                        <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                          <li>• Alle saker må dokumenteres i HMS-arbeidet</li>
                          <li>• Registrer saken i risikovurderinger</li>
                          <li>• Oppdater rutiner og prosedyrer ved behov</li>
                          <li>• Bruk erfaringene i HMS-opplæring</li>
                          <li>• Rapportér i årlig HMS-rapport</li>
                        </ul>
                      </div>
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 dark:text-white uppercase tracking-widest text-xs">Forebyggende arbeid</h4>
                        <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                          <li>• Vurder om saken avdekker systemsvikt</li>
                          <li>• Styrk rutiner for varsling og oppfølging</li>
                          <li>• Økt fokus på forebygging</li>
                          <li>• Gjennomfør tiltak for bedre arbeidsmiljø</li>
                          <li>• Evaluer og forbedre eksisterende systemer</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-gray-900 text-white p-6 rounded-2xl flex flex-wrap gap-2">
                      <LegalBadge text="AML §5-6" /> <LegalBadge text="AML §4-1 / §4-3" /> <LegalBadge text="AML kap. 2A" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer Navigation */}
            <div className="p-6 bg-gray-50 dark:bg-gray-900/80 border-t dark:border-gray-700 flex justify-between items-center">
              <button 
                onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
                disabled={activeStep === 0}
                className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 flex items-center gap-2"
              >
                <SafeIcon icon={FiChevronDown} className="rotate-90" /> Forrige
              </button>
              <div className="flex gap-1">
                {[0,1,2,3,4,5].map(i => (
                  <div key={i} className={`w-2 h-2 rounded-full ${activeStep === i ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'}`} />
                ))}
              </div>
              <button 
                onClick={() => setActiveStep(prev => Math.min(5, prev + 1))}
                disabled={activeStep === 5}
                className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-black shadow-lg hover:bg-blue-700 disabled:opacity-30 flex items-center gap-2"
              >
                Neste <SafeIcon icon={FiChevronDown} className="-rotate-90" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default InvestigationGuide;